import { useCallback } from 'react';
import { ToastType, useTosterStore } from '@store/toster';
import { allowedExtensions } from '@config/constant';

type MediaPayload = {
	folder: string;
	filename: string;
};

type HandleAddMediaReturn =
	| {
			payload: MediaPayload[];
			updatedFiles: File[];
	  }
	| undefined;

type AllowedMediaType = 'image' | 'video' | 'image / video';

type HandleAddMediaFunction = (
	event: React.ChangeEvent<HTMLInputElement>,
	currentFiles: File[],
	maxFileSize: number,
	maxFiles: number,
	folder: string,
	allowedType: AllowedMediaType
) => HandleAddMediaReturn;

type UseMediaUploadReturn = {
	handleUploadMedia: HandleAddMediaFunction;
};

export const useMediaUpload = (): UseMediaUploadReturn => {
	const { setToaster } = useTosterStore();

	const handleUploadMedia: HandleAddMediaFunction = useCallback(
		(event, currentFiles, maxFileSize, maxFiles, folder, allowedType): HandleAddMediaReturn => {
			const maxFileSizeMB = maxFileSize / (1024 * 1024);

			const files = event.target.files ? Array.from(event.target.files) : [];

			if (files.length === 0) return;

			const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
			const allowedVideoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];

			const invalidFiles = files.filter((file) => {
				if (allowedType === 'image') {
					return !allowedImageTypes.includes(file.type);
				}
				if (allowedType === 'video') {
					return !allowedVideoTypes.includes(file.type);
				}
				if (allowedType === 'image / video') {
					return ![...allowedImageTypes, ...allowedVideoTypes].includes(file.type);
				}
				return true;
			});

			if (invalidFiles.length > 0) {
				setToaster(true, ToastType.error, `Invalid file type. Allowed formats: ${allowedExtensions[allowedType]}.`);
				event.target.value = '';
				return;
			}

			const oversizedFiles = files.filter((file) => file.size > maxFileSize);

			if (oversizedFiles.length > 0) {
				setToaster(true, ToastType.error, `One or more files exceed the ${maxFileSizeMB}MB limit.`);
				event.target.value = '';
				return;
			}

			const totalFiles = (currentFiles?.length || 0) + files.length;

			if (totalFiles > maxFiles) {
				setToaster(true, ToastType.error, `Maximum ${maxFiles} files allowed.`);
				event.target.value = '';
				return;
			}

			const updatedFiles = [...currentFiles, ...files].slice(0, maxFiles);

			const payload: MediaPayload[] = updatedFiles.map((file) => ({
				folder,
				filename: file.name,
			}));

			return { payload, updatedFiles };
		},
		[setToaster]
	);

	return { handleUploadMedia };
};
